import React, { useEffect, useState, useContext } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import LogoutIcon from "@mui/icons-material/Logout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDAlert from "components/MDAlert";

import { LanguageContext } from "context";
import { useSessionContext } from "context/SessionContext";

import type { Session } from "types/session";

const API_BASE = import.meta.env.VITE_API_BASE_URL;

function Sessions(): JSX.Element {
  const { t } = useContext(LanguageContext) as any;
  const [sessions, setSessions] = useState([]);
  const currentUA = navigator.userAgent;

  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);

  const { session } = useSessionContext();

  useEffect(() => {
    (async () => {
      if (!session?.access_token) {
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/api/sessions`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
        });
        if (!res.ok) throw new Error("Unauthorized");
        const result = await res.json();
        const array = Array.isArray(result) ? result : result.sessions || [];
        setSessions(array);
      } catch (error) {
        setAlertMessage(t("Sttting-Sessions.sessin-error"));
        setShowAlert(true);
      }
    })();
  }, [session]);

  const isCurrentSession = (session: Session) => session.browser === currentUA;

  const handleDeleteSession = async (sessionId: number) => {
    if (!window.confirm(t("Sttting-Sessions.cofirm"))) return;

    if (!session?.access_token) {
        return;
    }

    const res = await fetch(`${API_BASE}/api/sessions/${sessionId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (res.ok) {
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } else {
      setAlertMessage(t("Sttting-Sessions.delete-error"));
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
    }
  };

  const getIcon = (type: string) => {
    if (type === "mobile") return "phone_iphone";
    if (type === "tablet") return "tablet_mac";
    return "desktop_windows";
  };

  return (
    <Card id="sessions">
      <MDBox p={3} lineHeight={1}>
        <MDTypography variant="h5">{t("Sttting-Sessions.title")}</MDTypography>
        <MDTypography variant="button" color="text" fontWeight="regular">
          {t("Sttting-Sessions.note")}
        </MDTypography>
      </MDBox>

      {showAlert && (
        <MDAlert color="error" dismissible>
          {alertMessage}
        </MDAlert>
      )}
      <MDBox pb={3} px={3} sx={{ overflow: "auto" }}>
        {sessions.map((session, index) => (
          <React.Fragment key={session.id}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width={{ xs: "max-content", sm: "100%" }}
            >
              <MDBox display="flex" alignItems="center">
                <MDBox textAlign="center" color="text" px={{ xs: 0, md: 1.5 }} opacity={0.6}>
                  <Icon>{getIcon(session.device_type)}</Icon>
                </MDBox>
                <MDBox ml={2}>
                  <MDTypography variant="button" fontWeight="medium">
                    {session.os} / {session.browser}
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    {/* IP: {session.ip_address} / {session.location} <br /> */}
                    IP: {session.ip_address}<br />
                    {t("Sttting-Sessions.last-access")} {new Date(session.last_accessed).toLocaleString()}
                    {isCurrentSession(session) && (
                      <MDTypography variant="caption" color="info" fontWeight="bold">
                        {t("Sttting-Sessions.device")}
                      </MDTypography>
                    )}
                  </MDTypography>
                </MDBox>
              </MDBox>

              <MDBox display="flex" alignItems="center">
                <MDBadge
                  variant="contained"
                  size="xs"
                  badgeContent={t("Sttting-Sessions.active")}
                  color="success"
                  container
                />
                {/* <MDBox mx={2} lineHeight={1}>
                  <MDTypography variant="button" color="secondary" fontWeight="regular">
                    {session.location}
                  </MDTypography>
                </MDBox> */}
                {!isCurrentSession(session) ? (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteSession(session.id)}
                    title={t("Sttting-Sessions.logout")}
                  >
                    <LogoutIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <MDTypography variant="caption" color="info" fontWeight="bold">
                    {t("Sttting-Sessions.current-device")}
                  </MDTypography>
                )}
              </MDBox>
            </MDBox>
            {index < sessions.length - 1 && <Divider sx={{ my: 2 }} />}
          </React.Fragment>
        ))}
      </MDBox>
    </Card>
  );
}

export default Sessions;
